import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiSave, FiPrinter, FiAlertCircle, FiChevronDown, FiChevronRight, FiEye, FiUpload, FiTrash2, FiImage, FiLayers, FiPlus, FiFileText } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { printDocument } from '../../utils/printTemplates';
import { invalidateCache } from '../../utils/fyDataCache';
import { CATALOGUE_COLUMNS, CATALOGUE_DESIGNS } from '../../utils/itemCataloguePrint';
import usePagePermissions from '../../hooks/usePagePermissions';
import { printReceipt } from '../pos/POSReceipt';
import { isRawBTEnabled, setRawBTEnabled, printViaRawBT, preloadLogoRaster } from '../../utils/escposReceipt';
import { printBarcodeLabels, DEFAULT_LABEL } from '../../utils/barcodeLabelPrint';
import { INVOICE_LETTERHEAD_MODULE, MAX_INVOICE_LETTERHEADS, EMPTY_INVOICE_LETTERHEAD, letterheadForChoice } from '../../utils/invoiceLetterheads';

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const FormCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${colors.borderRadius.md};
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: 24px;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'};
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  resize: vertical;
  min-height: 60px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TemplateCard = styled.div`
  border: 2px solid ${props => props.$active ? colors.primary.main : colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  padding: 16px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
  background: ${props => props.$active ? colors.primary.main + '08' : 'white'};

  &:hover {
    border-color: ${colors.primary.main};
  }
`;

const TemplateName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${colors.text.primary};
  margin-bottom: 4px;
`;

const TemplateDesc = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
`;

// Small section heading used to group the Sale Invoice field toggles.
const FieldGroupTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.secondary?.main || '#800020'};
  margin: 16px 0 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid ${colors.border?.light || '#eee'};
`;
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${colors.text.primary};
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: ${colors.neutral[300]};
    border-radius: 24px;
    transition: 0.2s;
  }

  span::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: 3px;
    transition: 0.2s;
  }

  input:checked + span {
    background: ${colors.primary.main};
  }

  input:checked + span::before {
    transform: translateX(20px);
  }
`;

const SectionDivider = styled.div`
  border-top: 1px solid ${colors.neutral[200]};
  margin: 20px 0;
`;

const DocOverrideHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: pointer;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:hover {
    color: ${colors.primary.main};
  }
`;

const DocOverrideName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DocOverrideActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  border-radius: ${colors.borderRadius.md};
  font-size: 12px;
  color: ${colors.primary.main};
  cursor: pointer;

  &:hover {
    background: ${colors.primary.main}20;
  }
`;

const DocOverrideBody = styled.div`
  padding: 16px 0 16px 20px;
`;

const SignatureInputs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const SignatureInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  width: 160px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const TEMPLATES = [
  { id: 'classic', name: 'Classic', desc: 'Accent border, colored headers, clean lines' },
  { id: 'modern', name: 'Modern', desc: 'Full color header band, rounded corners, muted table' },
  { id: 'detailed', name: 'Detailed', desc: 'Bordered sections, double-line dividers, formal' },
  { id: 'compact', name: 'Compact', desc: 'Smaller fonts, minimal margins, fits more on page' },
];

const DOC_TYPES = [
  { key: 'saleInvoice', name: 'Sale Invoice', columnSet: 'standard' },
  { key: 'purchaseInvoice', name: 'Purchase Invoice', columnSet: 'standard' },
  { key: 'nonTaxSaleInvoice', name: 'Non-Tax Sale Invoice', columnSet: 'standard' },
  { key: 'nonTaxPurchaseInvoice', name: 'Non-Tax Purchase Invoice', columnSet: 'standard' },
  { key: 'saleOrder', name: 'Sale Order', columnSet: 'standard' },
  { key: 'purchaseOrder', name: 'Purchase Order', columnSet: 'standard' },
  { key: 'quotation', name: 'Quotation', columnSet: 'standard' },
  { key: 'grn', name: 'Goods Receipt Note', columnSet: 'grn' },
  { key: 'deliveryChallan', name: 'Delivery Challan', columnSet: 'dc' },
  { key: 'saleReturn', name: 'Sale Return', columnSet: 'return' },
  { key: 'purchaseReturn', name: 'Purchase Return', columnSet: 'return' },
  { key: 'nonTaxSaleReturn', name: 'Non-Tax Sale Return', columnSet: 'return' },
  { key: 'nonTaxPurchaseReturn', name: 'Non-Tax Purchase Return', columnSet: 'return' },
  { key: 'importPurchaseInvoice', name: 'Import Purchase Invoice', columnSet: 'import' },
  // Vouchers (templates ready — live print functions in CR/BR/CP/BP/JV pages use inline rendering today)
  { key: 'cashReceipt', name: 'Cash Receipt', columnSet: 'voucher' },
  { key: 'bankReceipt', name: 'Bank Receipt', columnSet: 'voucher' },
  { key: 'cashPayment', name: 'Cash Payment', columnSet: 'voucher' },
  { key: 'bankPayment', name: 'Bank Payment', columnSet: 'voucher' },
  { key: 'journalVoucher', name: 'Journal Voucher', columnSet: 'journal' },
  // Salary
  { key: 'salaryAdvance', name: 'Salary Advance', columnSet: 'voucher' },
  { key: 'staffLoan', name: 'Staff Loan', columnSet: 'voucher' },
  { key: 'loanRecovery', name: 'Loan Recovery', columnSet: 'voucher' },
  { key: 'payslip', name: 'Salary Payslip', columnSet: 'payslip' },
  // Store / Production
  { key: 'addStock', name: 'Add Stock Adjustment', columnSet: 'stockAdjust' },
  { key: 'reduceStock', name: 'Reduce Stock Adjustment', columnSet: 'stockAdjust' },
  { key: 'stockTransfer', name: 'Stock Transfer', columnSet: 'stockAdjust' },
  { key: 'branchTransfer', name: 'Branch Transfer', columnSet: 'stockAdjust' },
  { key: 'purchaseRequisition', name: 'Purchase Requisition', columnSet: 'requisition' },
  { key: 'inwardGatePass', name: 'Inward Gate Pass', columnSet: 'gatePass' },
  { key: 'outwardGatePass', name: 'Outward Gate Pass', columnSet: 'gatePass' },
  { key: 'billOfMaterials', name: 'Bill of Materials', columnSet: 'bom' },
  { key: 'productionOrder', name: 'Production Order', columnSet: 'production' },
];

const STANDARD_COLUMNS = [
  { key: 'discountPercent', label: 'Discount %' },
  { key: 'tax', label: 'Tax' },
];

const RETURN_COLUMNS = [
  { key: 'tax', label: 'Tax' },
];

const DEFAULT_GLOBAL = {
  template: 'classic',
  accentColor: '#800020',
  logoPosition: 'left',
  showCompanyPhone: true,
  showCompanyEmail: true,
  showCompanyNTN: true,
  showCompanyGST: true,
  footerText: '',
  showSignatures: false,
  signatureLabels: ['Prepared By', 'Approved By', 'Received By'],
  duplicateCopy: false,
};

// POS Receipt is its own print path (POSReceipt.js renders an 80mm thermal HTML
// window) so it has a dedicated settings block separate from the global
// document templates. Every flag below maps 1:1 to a row in the thermal print.
// The defaults reproduce the legacy receipt verbatim so existing installs see
// no change until they explicitly customize.
const DEFAULT_POS_RECEIPT = {
  paperSize: '80mm',           // '58mm' | '80mm' | 'A4' | 'A5'
  receiptTitle: 'Sales Receipt',
  showLogo: false,
  logoSize: 'md',              // 'sm' | 'md' | 'lg'
  showCompanyName: true,
  showAddress: true,
  showPhone: true,
  showNTN: true,
  showGST: true,
  showWebsite: true,
  showCashier: true,
  showCustomer: true,          // hidden automatically for Walk-in
  showItemCode: false,
  showUoM: true,
  showDiscountColumn: true,
  showTaxBreakdown: true,
  showSubtotal: true,
  showPaymentMethod: true,
  showTenderedChange: true,
  showPreviousBalance: false,  // customer's outstanding + new balance (credit customers only)
  showBarcode: false,          // for return-scanning convenience
  headerText: '',
  footerText: 'Thank you for your purchase!',
  promoMessage: '',
  returnPolicyText: '',
  fontSize: 'medium',          // 'small' | 'medium' | 'large'
  boldTotal: true,
  copies: 1,
  currencyPosition: 'before'   // 'before' | 'after'
};

// Letterhead is supported only on the five sales documents listed below. POS
// receipts use a different print path (POSReceipt.js) and are not included.
// Other docs (PI, GRN, payments, journal, store ops) intentionally don't have
// the per-doc checkbox — letterhead is a customer-facing branding feature.
const LETTERHEAD_DOC_TYPES = [
  { key: 'quotation', name: 'Quotation' },
  { key: 'saleOrder', name: 'Sale Order' },
  { key: 'deliveryChallan', name: 'Delivery Challan' },
  { key: 'saleInvoice', name: 'Sale Invoice' },
  { key: 'saleReturn', name: 'Sale Return' },
];

const DEFAULT_LETTERHEAD = {
  headerUrl: null,
  footerUrl: null,
  watermarkUrl: null,
  // Storage paths kept alongside the URLs so we can delete the right object on replace/remove
  headerPath: null,
  footerPath: null,
  watermarkPath: null,
  headerHeightMm: 45,
  footerHeightMm: 30,
  watermarkOpacity: 0.08,
  watermarkWidthPct: 60,
  enabledForDocTypes: {
    quotation: true,
    saleOrder: true,
    deliveryChallan: true,
    saleInvoice: true,
    saleReturn: true,
  },
};

// 2 MB upload cap matches the brand-logo cap in SuperAdmin so users have one
// consistent limit. PNG/JPG/WebP only — SVG is excluded because it can carry
// scripts; transparent PNG is recommended for the watermark.
const LETTERHEAD_MAX_BYTES = 2 * 1024 * 1024;
const LETTERHEAD_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';

const LETTERHEAD_SLOTS = [
  {
    key: 'header',
    label: 'Header Image',
    icon: <FiLayers />,
    description: 'Top strip — logo, company name in English/Arabic, accent corners. Repeats on every page.',
    recommended: '2480 × 530 px (full A4 width × ~45 mm at 300 DPI)',
  },
  {
    key: 'footer',
    label: 'Footer Image',
    icon: <FiLayers style={{ transform: 'rotate(180deg)' }} />,
    description: 'Bottom strip — address, contact info, accent corners. Repeats on every page.',
    recommended: '2480 × 350 px (full A4 width × ~30 mm at 300 DPI)',
  },
  {
    key: 'watermark',
    label: 'Watermark',
    icon: <FiImage />,
    description: 'Faded image centered behind content. Transparent PNG works best.',
    recommended: '1500 × 1500 px square transparent PNG',
  },
];

const SAMPLE_DATA = {
  saleInvoice: { docNumber: 'SI-2025-00001', docDate: '2025-01-15', status: 'Approved', partyName: 'ABC Trading Co.', partyAddress: '123 Industrial Area', partyPhone: '0300-1234567', partyArea: 'SITE', partyRegion: 'Karachi', parentDocNumber: 'SO-2025-00001', dueDate: '2025-02-15', paymentTerms: 'Net 30', currency: 'PKR', subtotal: 50000, discountAmount: 2500, taxAmount: 8500, whtAmount: 1500, whtRate: 3, totalAmount: 54500, paidAmount: 20000, balanceAmount: 34500, notes: 'Thank you for your business.', lines: [{ description: 'Widget A - Premium', quantity: 10, unitPrice: 3000, discountPercent: 5, taxable: true, taxAmount: 4275, lineTotal: 32775 }, { description: 'Widget B - Standard', quantity: 5, unitPrice: 2000, discountPercent: 0, taxable: true, taxAmount: 1700, lineTotal: 11700 }] },
  purchaseInvoice: { docNumber: 'PI-2025-00001', docDate: '2025-01-15', status: 'Approved', partyName: 'XYZ Supplies Ltd.', vendorInvoiceNo: 'V-INV-1234', vendorInvoiceDate: '2025-01-10', parentDocNumber: 'GRN-2025-00001', poNumber: 'PO-2025-00001', dueDate: '2025-02-15', paymentTerms: 'Net 30', currency: 'PKR', subtotal: 40000, taxAmount: 6800, totalAmount: 46800, paidAmount: 0, balanceAmount: 46800, lines: [{ description: 'Raw Material A', quantity: 100, unitPrice: 250, discountPercent: 0, taxable: true, taxAmount: 4250, lineTotal: 29250 }, { description: 'Raw Material B', quantity: 50, unitPrice: 300, discountPercent: 0, taxable: true, taxAmount: 2550, lineTotal: 17550 }] },
  saleOrder: { docNumber: 'SO-2025-00001', docDate: '2025-01-10', status: 'Confirmed', partyName: 'ABC Trading Co.', customerPO: 'CPO-789', parentDocNumber: 'QT-2025-00001', deliveryDate: '2025-01-25', deliveryAddress: '123 Business St, Karachi', locationName: 'Main Warehouse', currency: 'PKR', subtotal: 50000, taxAmount: 8500, totalAmount: 58500, lines: [{ description: 'Widget A - Premium', quantity: 10, unitPrice: 3000, discountPercent: 5, taxable: true, taxAmount: 4275, lineTotal: 32775 }, { description: 'Widget B - Standard', quantity: 5, unitPrice: 2000, discountPercent: 0, taxable: true, taxAmount: 1700, lineTotal: 11700 }] },
  purchaseOrder: { docNumber: 'PO-2025-00001', docDate: '2025-01-08', status: 'Sent', partyName: 'XYZ Supplies Ltd.', referenceNo: 'REF-456', deliveryDate: '2025-01-20', paymentTerms: 'Net 30', deliveryAddress: 'Warehouse 2, Lahore', locationName: 'Main Warehouse', currency: 'PKR', subtotal: 40000, taxAmount: 6800, totalAmount: 46800, termsConditions: 'Delivery within 15 days.', lines: [{ description: 'Raw Material A', quantity: 100, unitPrice: 250, discountPercent: 0, taxable: true, taxAmount: 4250, lineTotal: 29250 }, { description: 'Raw Material B', quantity: 50, unitPrice: 300, discountPercent: 0, taxable: true, taxAmount: 2550, lineTotal: 17550 }] },
  quotation: { docNumber: 'QT-2025-00001', docDate: '2025-01-05', status: 'Sent', validUntil: '2025-02-05', partyName: 'ABC Trading Co.', customerRef: 'RFQ-123', locationName: 'Main Office', currency: 'PKR', subtotal: 50000, taxAmount: 8500, totalAmount: 58500, termsConditions: 'Prices valid for 30 days.', lines: [{ description: 'Widget A - Premium', quantity: 10, unitPrice: 3000, discountPercent: 5, taxable: true, taxAmount: 4275, lineTotal: 32775 }, { description: 'Widget B - Standard', quantity: 5, unitPrice: 2000, discountPercent: 0, taxable: true, taxAmount: 1700, lineTotal: 11700 }] },
  grn: { docNumber: 'GRN-2025-00001', docDate: '2025-01-18', status: 'Received', partyName: 'XYZ Supplies Ltd.', parentDocNumber: 'PO-2025-00001', locationName: 'Main Warehouse', vehicleNo: 'ABC-1234', totalAmount: 46800, lines: [{ description: 'Raw Material A', orderedQty: 100, receivedQty: 98, acceptedQty: 95, rejectedQty: 3, unitCost: 250, lineTotal: 23750 }, { description: 'Raw Material B', orderedQty: 50, receivedQty: 50, acceptedQty: 50, rejectedQty: 0, unitCost: 300, lineTotal: 15000 }] },
  deliveryChallan: { docNumber: 'DC-2025-00001', docDate: '2025-01-20', status: 'Dispatched', partyName: 'ABC Trading Co.', parentDocNumber: 'SO-2025-00001', vehicleNo: 'XYZ-5678', driverName: 'Ahmed Khan', driverPhone: '0300-1234567', deliveryAddress: '123 Business St, Karachi', subtotal: 50000, totalAmount: 50000, lines: [{ description: 'Widget A - Premium', orderedQty: 10, quantity: 10, unitPrice: 3000, lineTotal: 30000 }, { description: 'Widget B - Standard', orderedQty: 5, quantity: 5, unitPrice: 2000, lineTotal: 10000 }] },
  saleReturn: { docNumber: 'SCN-2025-00001', docDate: '2025-01-22', status: 'Approved', partyName: 'ABC Trading Co.', parentDocNumber: 'SI-2025-00001', reason: 'Defective items', subtotal: 6000, taxAmount: 1020, totalAmount: 7020, notes: 'Items returned due to quality issues.', lines: [{ description: 'Widget A - Premium', originalQty: 10, quantity: 2, unitPrice: 3000, taxable: true, taxAmount: 1020, lineTotal: 7020 }] },
  purchaseReturn: { docNumber: 'PDN-2025-00001', docDate: '2025-01-22', status: 'Approved', partyName: 'XYZ Supplies Ltd.', parentDocNumber: 'PI-2025-00001', reason: 'Wrong specification', subtotal: 5000, taxAmount: 850, whtAmount: 150, whtRate: 3, totalAmount: 5700, notes: 'Returning wrong items.', lines: [{ description: 'Raw Material A', originalQty: 100, returnQty: 20, quantity: 20, unitPrice: 250, taxable: true, taxAmount: 850, lineTotal: 5850 }] },
  nonTaxSaleInvoice: { docNumber: 'NSI-2025-00001', docDate: '2025-01-15', status: 'Approved', partyName: 'ABC Trading Co.', partyAddress: '123 Industrial Area', partyPhone: '0300-1234567', partyArea: 'SITE', partyRegion: 'Karachi', parentDocNumber: 'Direct', dueDate: '2025-02-15', paymentTerms: 'Net 30', currency: 'PKR', subtotal: 50000, discountAmount: 2500, taxAmount: 0, totalAmount: 47500, paidAmount: 20000, balanceAmount: 27500, notes: 'Non-tax invoice.', lines: [{ description: 'Widget A - Premium', quantity: 10, unitPrice: 3000, discountPercent: 5, taxable: false, taxAmount: 0, lineTotal: 28500 }, { description: 'Widget B - Standard', quantity: 5, unitPrice: 2000, discountPercent: 0, taxable: false, taxAmount: 0, lineTotal: 10000 }] },
  nonTaxPurchaseInvoice: { docNumber: 'NPI-2025-00001', docDate: '2025-01-15', status: 'Approved', partyName: 'XYZ Supplies Ltd.', vendorInvoiceNo: 'V-INV-1234', vendorInvoiceDate: '2025-01-10', parentDocNumber: 'Direct', dueDate: '2025-02-15', paymentTerms: 'Net 30', currency: 'PKR', subtotal: 40000, taxAmount: 0, whtAmount: 1200, whtRate: 3, totalAmount: 38800, paidAmount: 0, balanceAmount: 38800, lines: [{ description: 'Raw Material A', quantity: 100, unitPrice: 250, discountPercent: 0, taxable: false, taxAmount: 0, lineTotal: 25000 }, { description: 'Raw Material B', quantity: 50, unitPrice: 300, discountPercent: 0, taxable: false, taxAmount: 0, lineTotal: 15000 }] },
  nonTaxSaleReturn: { docNumber: 'NSR-2025-00001', docDate: '2025-01-22', status: 'Approved', partyName: 'ABC Trading Co.', parentDocNumber: 'NSI-2025-00001', reason: 'Defective items', subtotal: 6000, taxAmount: 0, totalAmount: 6000, notes: 'Items returned.', lines: [{ description: 'Widget A - Premium', originalQty: 10, quantity: 2, unitPrice: 3000, taxable: false, taxAmount: 0, lineTotal: 6000 }] },
  nonTaxPurchaseReturn: { docNumber: 'NPR-2025-00001', docDate: '2025-01-22', status: 'Approved', partyName: 'XYZ Supplies Ltd.', parentDocNumber: 'NPI-2025-00001', reason: 'Wrong specification', subtotal: 5000, taxAmount: 0, whtAmount: 150, whtRate: 3, totalAmount: 4850, notes: 'Returning wrong items.', lines: [{ description: 'Raw Material A', originalQty: 100, returnQty: 20, quantity: 20, unitPrice: 250, taxable: false, taxAmount: 0, lineTotal: 5000 }] },
  importPurchaseInvoice: {
    docNumber: 'IPI-2025-00001', docDate: '2025-01-15', status: 'Posted',
    partyName: 'Shanghai Tech Imports Co.',
    vendorInvoiceNo: 'INV-2025-882', vendorInvoiceDate: '2025-01-08',
    foreignCurrency: 'USD', exchangeRate: 280,
    gdNumber: 'GD-KHI-2025-44721', gdDate: '2025-01-14',
    blAwbNumber: 'MAEU 9988776', containerNumber: 'MSKU 1234567',
    shipmentMode: 'Sea', incoterms: 'CIF',
    countryOfOrigin: 'China', portOfLoading: 'Shanghai', portOfDischarge: 'Karachi',
    estimatedArrival: '2025-01-12', actualArrival: '2025-01-13',
    foreignSubtotal: 200000, baseSubtotal: 56000000,
    capitalizedTotal: 6300000, nonCapitalizedTotal: 10472000,
    inventoryValue: 62300000, totalAmount: 72772000,
    paidAmount: 0, balanceAmount: 72772000,
    subtotal: 56000000, taxAmount: 0, discountAmount: 0,
    notes: 'Container cleared on 13-Jan; goods received at Main Warehouse.',
    lines: [
      { itemCode: 'TV-55', itemName: 'TV 55-inch Smart', description: 'TV 55-inch Smart', unitOfMeasure: 'Pcs',
        quantity: 10, weight: 200, unitPriceForeign: 5000, foreignTotal: 50000,
        baseTotal: 14000000, capitalizedAllocated: 1960000, landedTotal: 15960000, landedUnitCost: 1596000 },
      { itemCode: 'SPK-X', itemName: 'Speaker X (Bluetooth)', description: 'Speaker X (Bluetooth)', unitOfMeasure: 'Pcs',
        quantity: 50, weight: 50, unitPriceForeign: 3000, foreignTotal: 150000,
        baseTotal: 42000000, capitalizedAllocated: 4340000, landedTotal: 46340000, landedUnitCost: 926800 },
    ],
    landedCharges: [
      { chargeName: 'Customs Duty', type: 'percentage', value: 10, base: 'cif', accountName: 'Customs Duty Payable',
        capitalize: true, allocationMethod: 'value', chargeAmount: 5600000 },
      { chargeName: 'Sales Tax', type: 'percentage', value: 17, base: 'cifPlusDuties', accountName: 'Customs Sales Tax Payable',
        claimAccountName: 'GST Input', capitalize: false, allocationMethod: 'value', chargeAmount: 10472000 },
      { chargeName: 'Freight', type: 'amount', value: 700000, base: 'manual', accountName: 'Freight Payable',
        capitalize: true, allocationMethod: 'weight', chargeAmount: 700000 },
    ],
  },
  // Vouchers
  cashReceipt: { docNumber: 'CR-2025-00001', docDate: '2025-01-20', status: 'Posted', partyName: 'ABC Trading Co.', paymentAccountName: 'Petty Cash', totalAmount: 25000, narration: 'Cash received against SI-2025-00001', referenceNo: 'Rec-001', currency: 'PKR' },
  bankReceipt: { docNumber: 'BR-2025-00001', docDate: '2025-01-20', status: 'Posted', partyName: 'ABC Trading Co.', paymentAccountName: 'HBL - 12345678', totalAmount: 150000, paymentMode: 'Cheque', chequeNo: '001234', chequeDate: '2025-01-20', narration: 'Partial payment against SI-2025-00001', currency: 'PKR' },
  cashPayment: { docNumber: 'CP-2025-00001', docDate: '2025-01-20', status: 'Posted', partyName: 'Office Expense', paymentAccountName: 'Petty Cash', totalAmount: 5000, narration: 'Office stationery', referenceNo: 'Vch-012', currency: 'PKR' },
  bankPayment: { docNumber: 'BP-2025-00001', docDate: '2025-01-20', status: 'Posted', partyName: 'XYZ Supplies Ltd.', paymentAccountName: 'HBL - 12345678', totalAmount: 80000, paymentMode: 'Bank Transfer', chequeNo: 'Trf-9988', chequeDate: '2025-01-20', narration: 'Payment against PI-2025-00001', currency: 'PKR' },
  journalVoucher: { docNumber: 'JV-2025-00001', docDate: '2025-01-20', status: 'Posted', narration: 'Monthly rent accrual', referenceNo: 'JV-Jan-Rent', totalDebit: 50000, totalCredit: 50000, currency: 'PKR',
    journalEntries: [
      { accountCode: '5201', accountName: 'Rent Expense', description: 'Jan rent', debitAmount: 50000, creditAmount: 0 },
      { accountCode: '2101', accountName: 'Rent Payable', description: 'Jan rent', debitAmount: 0, creditAmount: 50000 },
    ]
  },
  // Salary
  salaryAdvance: { docNumber: 'ADV-2025-00001', docDate: '2025-01-15', status: 'Issued', partyName: 'Ahmed Ali', totalAmount: 20000, recoveredAmount: 0, outstandingAmount: 20000, paymentMethod: 'cash', paymentAccountName: 'Petty Cash', reason: 'Medical emergency', currency: 'PKR' },
  loanRecovery: { docNumber: 'LRC-2025-00001', docDate: '2025-06-10', status: 'Posted', partyName: 'Fatima Khan', totalAmount: 50000, paymentMethod: 'cash', paymentAccountName: 'Cash in Hand', reference: 'Paid in office', currency: 'PKR',
    allocations: [{ transactionId: 'x', tranTypeCode: 'LON', docNumber: 'LON-2025-00001', balanceBefore: 100000, amount: 50000 }] },
  staffLoan: { docNumber: 'LON-2025-00001', docDate: '2025-01-15', status: 'Active', partyName: 'Fatima Khan', totalAmount: 120000, recoveredAmount: 20000, outstandingAmount: 100000, paymentMethod: 'bank', paymentAccountName: 'HBL - 12345678', purpose: 'Home purchase', numberOfInstallments: 12, monthlyInstallment: 10000, paidInstallments: 2, firstInstallmentMonth: '2025-02', currency: 'PKR' },
  payslip: { docNumber: 'PAY-2025-00001', docDate: '2025-01-31', status: 'Paid', payrollMonth: '2025-01', currency: 'PKR',
    line: { staffName: 'Ahmed Ali', staffCode: 'EMP-001', designation: 'Accountant', department: 'Finance',
      totalWorkingDays: 26, present: 24, absent: 1, paidLeave: 1, payableDays: 25,
      basicProrated: 48077, allowances: [{ name: 'House Rent', amountType: 'percent', value: 50, prorated: 24038 }, { name: 'Transport', amountType: 'fixed', value: 5000, prorated: 4808 }],
      bonus: 0, arrears: 0, overtime: 2500, leaveDeduction: 0, adjustedGross: 79423,
      deductions: [{ name: 'Income Tax', computed: 3500 }, { name: 'EOBI', computed: 370 }],
      loanInstallment: 10000, advanceRecovery: 0, totalDeductions: 13870, netPayable: 65553 }
  },
  // Store / Production
  addStock: { docNumber: 'AS-2025-00001', docDate: '2025-01-10', status: 'Posted', adjustmentType: 'Physical Count', reason: 'Year-end stock take', locationName: 'Main Warehouse', employeeName: 'Storekeeper A', totalAmount: 45000, currency: 'PKR',
    lines: [{ itemCode: 'IT-001', itemName: 'Widget A', quantity: 10, unitCost: 3000, lineTotal: 30000 }, { itemCode: 'IT-002', itemName: 'Widget B', quantity: 5, unitCost: 3000, lineTotal: 15000 }] },
  reduceStock: { docNumber: 'RS-2025-00001', docDate: '2025-01-10', status: 'Posted', adjustmentType: 'Damage', reason: 'Water damage', locationName: 'Main Warehouse', employeeName: 'Storekeeper A', totalAmount: 9000, currency: 'PKR',
    lines: [{ itemCode: 'IT-001', itemName: 'Widget A', quantity: 3, unitCost: 3000, lineTotal: 9000 }] },
  stockTransfer: { docNumber: 'ST-2025-00001', docDate: '2025-01-12', status: 'Completed', fromLocationName: 'Main Warehouse', toLocationName: 'Shop Floor', employeeName: 'Driver A', totalAmount: 15000, currency: 'PKR',
    lines: [{ itemCode: 'IT-001', itemName: 'Widget A', quantity: 5, unitCost: 3000, lineTotal: 15000 }] },
  branchTransfer: { docNumber: 'BT-2025-00001', docDate: '2025-01-12', status: 'Completed', fromBranchName: 'Karachi HO', toBranchName: 'Lahore Branch', fromLocationName: 'Main Warehouse', toLocationName: 'Lahore Warehouse', employeeName: 'Driver B', totalAmount: 30000, currency: 'PKR',
    lines: [{ itemCode: 'IT-001', itemName: 'Widget A', quantity: 10, unitCost: 3000, lineTotal: 30000 }] },
  purchaseRequisition: { docNumber: 'REQ-2025-00001', docDate: '2025-01-08', status: 'Approved', requestedBy: 'Ops Manager', department: 'Operations', priority: 'High', requiredDate: '2025-01-25', locationName: 'Main Warehouse', currency: 'PKR',
    lines: [{ itemCode: 'IT-001', itemName: 'Widget A', quantity: 50, notes: 'Urgent top-up' }] },
  inwardGatePass: { docNumber: 'IGP-2025-00001', docDate: '2025-01-14', docTime: '10:30', status: 'Approved', partyName: 'XYZ Supplies Ltd.', purpose: 'Goods delivery', vehicleNo: 'KHI-1234', driverName: 'M. Ali', receivedBy: 'Security A', locationName: 'Main Warehouse',
    lines: [{ itemName: 'Raw Material A', quantity: 100, unitOfMeasure: 'Kg' }] },
  outwardGatePass: { docNumber: 'OGP-2025-00001', docDate: '2025-01-14', docTime: '16:00', status: 'Approved', destination: 'ABC Trading Co.', purpose: 'Finished goods delivery', vehicleNo: 'KHI-5678', driverName: 'M. Sajjad', authorizedBy: 'Store Manager', locationName: 'Main Warehouse',
    lines: [{ itemName: 'Widget A', quantity: 10, unitOfMeasure: 'Pcs' }] },
  billOfMaterials: { docNumber: 'BOM-2025-00001', bomName: 'Widget A Recipe', finishedItemName: 'Widget A - Premium', outputQuantity: 1, outputUom: 'Pcs', productionType: 'In-House', version: '1.0', effectiveFrom: '2025-01-01', materialCost: 1800, overheadCost: 700, outsourcingCost: 0, totalCost: 2500, costPerUnit: 2500, currency: 'PKR',
    lines: [{ itemCode: 'RM-001', itemName: 'Raw Material A', quantity: 0.5, unitOfMeasure: 'Kg', unitCost: 1500, lineTotal: 750 }, { itemCode: 'RM-002', itemName: 'Raw Material B', quantity: 1, unitOfMeasure: 'Kg', unitCost: 1050, lineTotal: 1050 }] },
  productionOrder: { docNumber: 'PRO-2025-00001', docDate: '2025-01-15', status: 'Completed', bomDocNumber: 'BOM-2025-00001', finishedItemName: 'Widget A - Premium', productionType: 'In-House', plannedQty: 100, actualQty: 98, outputUom: 'Pcs', completionDate: '2025-01-18', locationName: 'Production Floor',
    estMaterialCost: 180000, actualMaterialCost: 182500, materialVariance: 2500,
    estOverheadCost: 70000, actualOverheadCost: 68000, overheadVariance: -2000,
    estOutsourcingCost: 0, actualOutsourcingCost: 0, outsourcingVariance: 0,
    estTotalCost: 250000, actualTotalCost: 250500, totalVariance: 500,
    estCostPerUnit: 2500, actualCostPerUnit: 2556, currency: 'PKR',
    lines: [{ itemCode: 'RM-001', itemName: 'Raw Material A', plannedQty: 50, actualQty: 51, unitCost: 1500 }, { itemCode: 'RM-002', itemName: 'Raw Material B', plannedQty: 100, actualQty: 102, unitCost: 1050 }] },
};

// ─── Component ────────────────────────────────────────────────────────────────

const PrintSettings = () => {
  const { activeCompany, currentUser, activeBranch, isModuleEnabled, branches } = useAuth();
  // "Show Branch Name" appears whenever branches are on — even with one branch,
  // whose name still prints at the top (the list is empty when branches are off).
  const hasBranches = Array.isArray(branches) && branches.length > 0;
  const barcodeLabelsEnabled = !!(isModuleEnabled && isModuleEnabled('master_barcode_labels'));
  // Advance module: extra letterheads to choose from when printing invoices.
  const invoiceLetterheadsOn = !!(isModuleEnabled && isModuleEnabled(INVOICE_LETTERHEAD_MODULE));
  const { canEdit } = usePagePermissions('settings', 'printFormats');
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const [settings, setSettings] = useState({
    global: { ...DEFAULT_GLOBAL },
    letterhead: { ...DEFAULT_LETTERHEAD },
    posReceipt: { ...DEFAULT_POS_RECEIPT },
    barcodeLabel: { ...DEFAULT_LABEL }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  // Per-DEVICE Bluetooth-thermal (RawBT) switch — stored in localStorage, not in
  // the company settings, so only THIS device routes POS receipts to RawBT.
  const [rawbtOn, setRawbtOn] = useState(isRawBTEnabled());
  const [expandedDocs, setExpandedDocs] = useState({});
  // Per-slot upload state (header / footer / watermark) — independently disable
  // the upload button while one image is being uploaded, without blocking the others.
  const [uploadingSlot, setUploadingSlot] = useState(null);

  const loadSettings = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const ref = doc(db, 'companies', activeCompany.id, 'settings', 'documentFormats');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setSettings({
          global: { ...DEFAULT_GLOBAL, ...data.global },
          letterhead: {
            ...DEFAULT_LETTERHEAD,
            ...(data.letterhead || {}),
            // Merge the enabledForDocTypes map carefully so a saved partial map
            // (e.g. only saleInvoice present) doesn't drop the default ON state
            // for the other four sales doc types — but does honour explicit OFFs.
            enabledForDocTypes: {
              ...DEFAULT_LETTERHEAD.enabledForDocTypes,
              ...(data.letterhead?.enabledForDocTypes || {}),
            },
          },
          // POS receipt settings: merge stored values onto defaults so newly
          // added flags get their default ON/OFF state on legacy installs.
          posReceipt: { ...DEFAULT_POS_RECEIPT, ...(data.posReceipt || {}) },
          // Barcode label settings: same defaults-merge pattern. Card only
          // renders when the `master_barcode_labels` module is enabled.
          barcodeLabel: { ...DEFAULT_LABEL, ...(data.barcodeLabel || {}) },
          // Invoice Letterhead Choice list — kept only when the company has one.
          ...(Array.isArray(data.invoiceLetterheads) ? { invoiceLetterheads: data.invoiceLetterheads } : {}),
          ...DOC_TYPES.reduce((acc, dt) => {
            acc[dt.key] = data[dt.key] || {};
            return acc;
          }, {}),
        });
      }
    } catch (error) {
      console.error('Error loading print settings:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to load print settings' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const updateGlobal = (key, value) => {
    setSettings(prev => ({ ...prev, global: { ...prev.global, [key]: value } }));
  };

  const updateDocOverride = (docKey, key, value) => {
    setSettings(prev => ({
      ...prev,
      [docKey]: { ...prev[docKey], [key]: value },
    }));
  };

  const updateDocColumn = (docKey, colKey, value) => {
    setSettings(prev => ({
      ...prev,
      [docKey]: {
        ...prev[docKey],
        columns: { ...(prev[docKey]?.columns || {}), [colKey]: value },
      },
    }));
  };

  const updateSignatureLabel = (index, value) => {
    const labels = [...(settings.global.signatureLabels || DEFAULT_GLOBAL.signatureLabels)];
    labels[index] = value;
    updateGlobal('signatureLabels', labels);
  };

  // ─── Letterhead handlers ──────────────────────────────────────────────────
  // Image uploads write to Firestore IMMEDIATELY (not on form submit) so a user
  // who closes the page mid-upload doesn't leave an orphan file in Storage.
  // The form's Save button only persists the toggleable fields (margins, opacity,
  // per-doc checkboxes). This matches the pattern used by SuperAdmin's brand logo.
  const persistLetterheadFields = async (patch) => {
    if (!activeCompany?.id) return;
    const ref = doc(db, 'companies', activeCompany.id, 'settings', 'documentFormats');
    // Fan the patch into a flat dotted-key update so we don't clobber other settings.
    const updates = {};
    Object.entries(patch).forEach(([k, v]) => { updates[`letterhead.${k}`] = v; });
    updates.updatedAt = new Date().toISOString();
    updates.updatedBy = currentUser?.uid;
    try {
      await updateDoc(ref, updates);
    } catch (e) {
      // First-time save: doc may not exist yet — fall back to setDoc with merge.
      const snap = await getDoc(ref);
      const existing = snap.exists() ? snap.data() : {};
      await setDoc(ref, {
        ...existing,
        letterhead: { ...DEFAULT_LETTERHEAD, ...(existing.letterhead || {}), ...patch },
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid,
      });
    }
    // loadDocumentFormats() caches this doc for 30 min — drop the entry so the
    // next print on this device uses the just-saved layout, not the old one.
    invalidateCache(`docFormats|${activeCompany.id}`);
  };

  const handleLetterheadUpload = async (slot, file) => {
    if (!file) return;
    if (!canEdit) {
      setAlert({ show: true, type: 'error', message: 'You do not have permission to upload letterhead images.' });
      return;
    }
    if (!file.type || !LETTERHEAD_ACCEPT.split(',').includes(file.type.toLowerCase())) {
      setAlert({ show: true, type: 'error', message: 'Unsupported format. Please upload PNG, JPG or WebP.' });
      return;
    }
    if (file.size > LETTERHEAD_MAX_BYTES) {
      setAlert({ show: true, type: 'error', message: 'Image is larger than 2 MB. Please compress and try again.' });
      return;
    }

    setUploadingSlot(slot);
    setAlert({ show: false, type: '', message: '' });

    const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
    const path = `companies/${activeCompany.id}/letterhead/${slot}_${Date.now()}.${ext}`;

    // Capture the previous storage path BEFORE we overwrite — we'll delete it after
    // the new upload succeeds so a failed upload never strands the user without an image.
    const prevPath = settings.letterhead?.[`${slot}Path`] || null;

    try {
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: file.type });
      const url = await getDownloadURL(ref);

      const patch = {
        [`${slot}Url`]: url,
        [`${slot}Path`]: path,
      };
      await persistLetterheadFields(patch);
      setSettings(prev => ({ ...prev, letterhead: { ...prev.letterhead, ...patch } }));

      // Clean up the previous file. If this fails (already deleted, race, etc.)
      // we just log — the new image is already live and that's what matters.
      if (prevPath) {
        try { await deleteObject(storageRef(storage, prevPath)); } catch (e) { /* swallow */ }
      }
    } catch (err) {
      console.error('Letterhead upload failed:', err);
      setAlert({ show: true, type: 'error', message: 'Upload failed: ' + (err?.message || 'unknown error') });
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleLetterheadRemove = async (slot) => {
    if (!canEdit) return;
    const path = settings.letterhead?.[`${slot}Path`];
    const patch = { [`${slot}Url`]: null, [`${slot}Path`]: null };
    try {
      await persistLetterheadFields(patch);
      setSettings(prev => ({ ...prev, letterhead: { ...prev.letterhead, ...patch } }));
      if (path) {
        try { await deleteObject(storageRef(storage, path)); } catch (e) { /* swallow */ }
      }
    } catch (err) {
      console.error('Letterhead remove failed:', err);
      setAlert({ show: true, type: 'error', message: 'Could not remove image: ' + (err?.message || 'unknown error') });
    }
  };

  const updateLetterhead = (key, value) => {
    setSettings(prev => ({ ...prev, letterhead: { ...prev.letterhead, [key]: value } }));
  };

  const toggleLetterheadDoc = (docKey) => {
    setSettings(prev => ({
      ...prev,
      letterhead: {
        ...prev.letterhead,
        enabledForDocTypes: {
          ...(prev.letterhead?.enabledForDocTypes || {}),
          [docKey]: !(prev.letterhead?.enabledForDocTypes?.[docKey]),
        },
      },
    }));
  };

  const toggleDocExpanded = (docKey) => {
    setExpandedDocs(prev => ({ ...prev, [docKey]: !prev[docKey] }));
  };

  const handlePreview = (docKey) => {
    printDocument(docKey, SAMPLE_DATA[docKey], activeCompany, settings, null);
  };

  // ─── Invoice Letterheads (advance module) ─────────────────────────────────
  const invoiceLetterheads = settings.invoiceLetterheads || [];
  const setInvoiceLetterheads = (list) => setSettings(prev => ({ ...prev, invoiceLetterheads: list }));
  const addInvoiceLetterhead = () => {
    if (invoiceLetterheads.length >= MAX_INVOICE_LETTERHEADS) return;
    setInvoiceLetterheads([...invoiceLetterheads, { id: `lh_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, ...EMPTY_INVOICE_LETTERHEAD }]);
  };
  const updateInvoiceLetterhead = (id, key, value) => setInvoiceLetterheads(invoiceLetterheads.map(l => (l.id === id ? { ...l, [key]: value } : l)));
  const removeInvoiceLetterhead = (id) => setInvoiceLetterheads(invoiceLetterheads.filter(l => l.id !== id));
  const previewInvoiceLetterhead = (choice) => {
    printDocument('saleInvoice', SAMPLE_DATA.saleInvoice, activeCompany, settings, null, { letterhead: letterheadForChoice(choice, invoiceLetterheads) });
  };

  const updatePOSReceipt = (key, value) => {
    setSettings(prev => ({ ...prev, posReceipt: { ...prev.posReceipt, [key]: value } }));
  };

  const updateBarcodeLabel = (key, value) => {
    setSettings(prev => ({ ...prev, barcodeLabel: { ...prev.barcodeLabel, [key]: value } }));
  };

  // Renders three sample labels using the current (unsaved) barcode-label
  // config so the user can iterate without committing first.
  const handleBarcodeLabelPreview = () => {
    const sample = [
      { item: { itemName: 'Coca Cola 500ml',  itemCode: 'SKU-001', salePrice: 120, mrp: 130, barcode: '2000000000017' }, qty: 1 },
      { item: { itemName: 'Lays Salted',       itemCode: 'SKU-002', salePrice: 50,  mrp: 60,  barcode: '2000000000024' }, qty: 1 },
      { item: { itemName: 'Dairy Milk Chocolate Bar 50g', itemCode: 'SKU-003', salePrice: 180, mrp: 200, barcode: '2000000000031' }, qty: 1 },
    ];
    printBarcodeLabels(sample, activeCompany, settings.barcodeLabel);
  };

  // Renders a sample POS receipt using the current (unsaved) settings so the
  // user can iterate on the design without committing first.
  const handlePOSReceiptPreview = () => {
    const sample = {
      docNumber: 'PS-2026-00042',
      docDate: new Date().toISOString().split('T')[0],
      cart: [
        { itemCode: 'SKU-001', itemName: 'Coca Cola 500ml', quantity: 2, unitPrice: 120, unitOfMeasure: 'Pcs', lineTotal: 240, discountPercent: 0 },
        { itemCode: 'SKU-002', itemName: 'Lays Salted',    quantity: 3, unitPrice: 50,  unitOfMeasure: 'Pcs', lineTotal: 150, discountPercent: 0 },
        { itemCode: 'SKU-003', itemName: 'Dairy Milk Bar',  quantity: 1, unitPrice: 180, unitOfMeasure: 'Pcs', lineTotal: 180, discountPercent: 10 }
      ],
      subtotal: 570,
      discountAmount: 18,
      taxAmount: 0,
      totalAmount: 552,
      paymentMethod: 'cash',
      payments: [{ method: 'cash', amount: 300 }, { method: 'credit', amount: 252 }],
      cashTendered: 300,
      changeGiven: 0,
      // Named customer + prior dues so the "Previous Balance" block can be previewed
      // when that toggle is on (it stays hidden for Walk-in customers).
      customerName: 'Ahmed Traders (Demo)',
      previousBalance: 12500,
      cashierName: 'Demo Cashier'
    };
    printReceipt(sample, activeCompany, settings.posReceipt);
  };

  // Flip the per-device RawBT switch. When turning ON, warm the logo raster so
  // the very first receipt already carries the logo.
  const handleToggleRawBT = (on) => {
    setRawBTEnabled(on);
    setRawbtOn(on);
    if (on) {
      const url = activeCompany?.logoUrl || activeCompany?.brandLogoUrl;
      const dots = (settings.posReceipt?.paperSize === '80mm') ? 576 : 384;
      if (url && settings.posReceipt?.showLogo) preloadLogoRaster(url, dots);
    }
  };

  // Send a sample receipt straight to RawBT so the user can confirm the printer
  // fires (independent of the on/off switch above).
  const handleTestRawBT = () => {
    const sample = {
      docNumber: 'TEST-0001',
      docDate: new Date().toISOString().split('T')[0],
      cart: [
        { itemCode: 'SKU-001', itemName: 'Coca Cola 500ml', quantity: 2, unitPrice: 120, unitOfMeasure: 'Pcs', lineTotal: 240 },
        { itemCode: 'SKU-002', itemName: 'Lays Salted', quantity: 3, unitPrice: 50, unitOfMeasure: 'Pcs', lineTotal: 150 },
      ],
      subtotal: 390, discountAmount: 0, taxAmount: 0, totalAmount: 390,
      paymentMethod: 'cash', payments: [{ method: 'cash', amount: 400 }],
      cashTendered: 400, changeGiven: 10,
      customerName: 'Walk-in Customer', cashierName: 'Test',
    };
    printViaRawBT(sample, activeCompany, { ...DEFAULT_POS_RECEIPT, ...(settings.posReceipt || {}) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if ((settings.invoiceLetterheads || []).some(l => !String(l.name || '').trim() && Object.keys(EMPTY_INVOICE_LETTERHEAD).some(k => String(l[k] || '').trim()))) {
      setAlert({ show: true, type: 'error', message: 'Each invoice letterhead needs a Name.' });
      return;
    }
    setSaving(true);
    try {
      const ref = doc(db, 'companies', activeCompany.id, 'settings', 'documentFormats');
      await setDoc(ref, {
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid,
      });
      // loadDocumentFormats() caches this doc for 30 min — drop the entry so the
      // next print on this device uses the just-saved layout, not the old one.
      invalidateCache(`docFormats|${activeCompany.id}`);
      setAlert({ show: true, type: 'success', message: 'Print settings saved successfully' });
    } catch (error) {
      console.error('Error saving print settings:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save print settings' });
    } finally {
      setSaving(false);
    }
  };

  const g = settings.global || DEFAULT_GLOBAL;

  const getColumnsForDocType = (docType) => {
    if (docType.columnSet === 'standard') return STANDARD_COLUMNS;
    if (docType.columnSet === 'return') return RETURN_COLUMNS;
    return [];
  };

  return (
    <PageLayout pageTitle="Print Formats">
      <Container>
        <PageHeader>
          <PageTitle>Print Format Settings</PageTitle>
          <PageDescription>
            Customize print templates, accent colors, and per-document layout options for all transaction types.
          </PageDescription>
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            <FiAlertCircle />
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <FormCard>
            <CardBody style={{ textAlign: 'center', padding: '60px 20px' }}>
              Loading settings...
            </CardBody>
          </FormCard>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* ─── Global Defaults ─── */}
            <FormCard>
              <CardHeader>
                <CardIcon><FiPrinter /></CardIcon>
                <CardTitle>Global Defaults</CardTitle>
              </CardHeader>
              <CardBody>
                <Label style={{ marginBottom: 12 }}>Template Style</Label>
                <TemplateGrid>
                  {TEMPLATES.map(t => (
                    <TemplateCard
                      key={t.id}
                      $active={g.template === t.id}
                      onClick={() => updateGlobal('template', t.id)}
                    >
                      <TemplateName>{t.name}</TemplateName>
                      <TemplateDesc>{t.desc}</TemplateDesc>
                    </TemplateCard>
                  ))}
                </TemplateGrid>

                <FormGrid>
                  <FormGroup>
                    <Label>Accent Color</Label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="color"
                        value={g.accentColor || '#800020'}
                        onChange={(e) => updateGlobal('accentColor', e.target.value)}
                        style={{ width: 40, height: 36, border: 'none', cursor: 'pointer' }}
                      />
                      <Input
                        value={g.accentColor || '#800020'}
                        onChange={(e) => updateGlobal('accentColor', e.target.value)}
                        style={{ width: 100 }}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label>Logo Position</Label>
                    <Select
                      value={g.logoPosition || 'left'}
                      onChange={(e) => updateGlobal('logoPosition', e.target.value)}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                      <option value="hidden">Hidden</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Company Info Visibility</Label>
                {hasBranches && (
                  <ToggleRow>
                    <ToggleLabel>Show Branch Name (off = only the company name prints at the top)</ToggleLabel>
                    <Toggle><input type="checkbox" checked={g.showBranchName !== false} onChange={(e) => updateGlobal('showBranchName', e.target.checked)} /><span /></Toggle>
                  </ToggleRow>
                )}
                <ToggleRow>
                  <ToggleLabel>Show Phone Number</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.showCompanyPhone !== false} onChange={(e) => updateGlobal('showCompanyPhone', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Email</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.showCompanyEmail !== false} onChange={(e) => updateGlobal('showCompanyEmail', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show NTN</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.showCompanyNTN !== false} onChange={(e) => updateGlobal('showCompanyNTN', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show {taxLabel} Number</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.showCompanyGST !== false} onChange={(e) => updateGlobal('showCompanyGST', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <FormGroup>
                  <Label>Footer Text</Label>
                  <TextArea
                    value={g.footerText || ''}
                    onChange={(e) => updateGlobal('footerText', e.target.value)}
                    placeholder="Optional text shown at the bottom of every printed document"
                  />
                </FormGroup>

                <SectionDivider />
                <FormGroup>
                  <Label>Notes / Remarks Position</Label>
                  <Select
                    value={g.notesPosition === 'top' ? 'top' : 'bottom'}
                    onChange={(e) => updateGlobal('notesPosition', e.target.value)}
                  >
                    <option value="bottom">Below totals (default)</option>
                    <option value="top">Above items table</option>
                  </Select>
                  <div style={{ fontSize: 12, color: colors.text.secondary, marginTop: 4 }}>
                    Applies to all printed invoices, orders, quotations, and delivery challans. Terms &amp; Conditions and Reason always stay in the footer.
                  </div>
                </FormGroup>

                <SectionDivider />
                <ToggleRow>
                  <ToggleLabel>Show Signature Lines</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.showSignatures || false} onChange={(e) => updateGlobal('showSignatures', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                {g.showSignatures && (
                  <div>
                    <Label style={{ marginTop: 10 }}>Signature Labels</Label>
                    <SignatureInputs>
                      {(g.signatureLabels || DEFAULT_GLOBAL.signatureLabels).map((label, idx) => (
                        <SignatureInput
                          key={idx}
                          value={label}
                          onChange={(e) => updateSignatureLabel(idx, e.target.value)}
                          placeholder={`Signature ${idx + 1}`}
                        />
                      ))}
                    </SignatureInputs>
                  </div>
                )}

                <SectionDivider />
                <ToggleRow>
                  <ToggleLabel>Print Duplicate Copy (2 per page — cut from middle)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.duplicateCopy || false} onChange={(e) => updateGlobal('duplicateCopy', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <ToggleRow>
                  <ToggleLabel>Show Print Preview (with Print and Download PDF buttons)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={g.printPreview !== false} onChange={(e) => updateGlobal('printPreview', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <div style={{ fontSize: 12, color: colors.text.secondary, marginTop: 4 }}>
                  On: printing a document opens a preview on the same screen first. Off: the print window opens straight away, as before. (POS receipts always print directly.)
                </div>
              </CardBody>
            </FormCard>

            {/* ─── Per-Document Overrides ─── */}
            <FormCard>
              <CardHeader>
                <CardIcon><FiPrinter /></CardIcon>
                <CardTitle>Per-Document Overrides</CardTitle>
              </CardHeader>
              <CardBody>
                {DOC_TYPES.map(dt => {
                  const isExpanded = expandedDocs[dt.key];
                  const docSettings = settings[dt.key] || {};
                  const columnDefs = getColumnsForDocType(dt);

                  return (
                    <div key={dt.key}>
                      <DocOverrideHeader onClick={() => toggleDocExpanded(dt.key)}>
                        <DocOverrideName>
                          {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                          {dt.name}
                        </DocOverrideName>
                        <DocOverrideActions>
                          <PreviewButton onClick={(e) => { e.stopPropagation(); handlePreview(dt.key); }}>
                            <FiEye size={12} /> Preview
                          </PreviewButton>
                        </DocOverrideActions>
                      </DocOverrideHeader>

                      {isExpanded && (
                        <DocOverrideBody>
                          <FormGroup style={{ marginBottom: 12 }}>
                            <Label>Template Override</Label>
                            <Select
                              value={docSettings.template || ''}
                              onChange={(e) => updateDocOverride(dt.key, 'template', e.target.value || null)}
                            >
                              <option value="">Use Global ({g.template})</option>
                              {TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </Select>
                          </FormGroup>

                          <FormGroup style={{ marginBottom: 12 }}>
                            <Label>Printed Title / Heading</Label>
                            <Input
                              type="text"
                              value={docSettings.customTitle || ''}
                              placeholder={`Default: ${dt.name.toUpperCase()}`}
                              onChange={(e) => updateDocOverride(dt.key, 'customTitle', e.target.value)}
                            />
                            <PageDescription style={{ marginTop: 6 }}>
                              Leave blank to use the default heading. Set your own to print e.g. "ESTIMATE INVOICE" or "SALE BILL" instead.
                            </PageDescription>
                          </FormGroup>

                          {(dt.key === 'saleInvoice' || dt.key === 'nonTaxSaleInvoice') && (
                            <>
                              <FormGroup style={{ marginBottom: 12 }}>
                                <Label>Paper Size</Label>
                                <Select
                                  value={docSettings.paperSize || 'A4'}
                                  onChange={(e) => updateDocOverride(dt.key, 'paperSize', e.target.value)}
                                >
                                  <option value="A4">A4</option>
                                  <option value="A5P">A5 Portrait</option>
                                  <option value="A5L">A5 Landscape</option>
                                </Select>
                                <PageDescription style={{ marginTop: 6 }}>
                                  A5 applies to plain invoices only — if Letterhead or Duplicate Copy is on, it prints A4.
                                </PageDescription>
                              </FormGroup>
                              <ToggleRow>
                                <ToggleLabel>Show Trade Name (Tagline)</ToggleLabel>
                                <Toggle><input type="checkbox" checked={docSettings.showTradeName === true} onChange={(e) => updateDocOverride(dt.key, 'showTradeName', e.target.checked)} /><span /></Toggle>
                              </ToggleRow>
                            </>
                          )}

                          {/* Company Details for the Non-Tax Sale Invoice only (2026-11-05).
                              Each switch starts at what prints today (the general setting). */}
                          {dt.key === 'nonTaxSaleInvoice' && (
                            <>
                              <FieldGroupTitle>Company Details</FieldGroupTitle>
                              <PageDescription style={{ marginTop: 0, marginBottom: 8 }}>
                                Show or hide your company's details at the top of the Non-Tax Sale Invoice. Other documents keep the general settings.
                              </PageDescription>
                              {[
                                ['coLogo', 'Show Logo', (g.logoPosition || 'left') !== 'hidden'],
                                ['coName', 'Show Company Name', true],
                                ['coAddress', 'Show Address', true],
                                ['coPhone', 'Show Phone', g.showCompanyPhone !== false],
                                ['coEmail', 'Show Email', g.showCompanyEmail !== false],
                                ['coNTN', 'Show NTN', g.showCompanyNTN !== false],
                                ['coSTN', `Show ${taxLabel} Number`, g.showCompanyGST !== false],
                              ].map(([k, label, def]) => (
                                <ToggleRow key={k}>
                                  <ToggleLabel>{label}</ToggleLabel>
                                  <Toggle><input type="checkbox" checked={docSettings[k] ?? def} onChange={(e) => updateDocOverride(dt.key, k, e.target.checked)} /><span /></Toggle>
                                </ToggleRow>
                              ))}
                            </>
                          )}

                          {(dt.key === 'saleInvoice' || dt.key === 'purchaseInvoice' || dt.key === 'nonTaxSaleInvoice' || dt.key === 'nonTaxPurchaseInvoice') && (
                            <>
                              <ToggleRow>
                                <ToggleLabel>Show Payment Info (Paid / Balance)</ToggleLabel>
                                <Toggle><input type="checkbox" checked={docSettings.showPaymentInfo !== false} onChange={(e) => updateDocOverride(dt.key, 'showPaymentInfo', e.target.checked)} /><span /></Toggle>
                              </ToggleRow>
                              {(dt.key === 'saleInvoice' || dt.key === 'purchaseInvoice' || dt.key === 'nonTaxPurchaseInvoice') && (
                                <ToggleRow>
                                  <ToggleLabel>Show WHT</ToggleLabel>
                                  <Toggle><input type="checkbox" checked={docSettings.showWHT !== false} onChange={(e) => updateDocOverride(dt.key, 'showWHT', e.target.checked)} /><span /></Toggle>
                                </ToggleRow>
                              )}
                              {dt.key === 'nonTaxSaleInvoice' && (
                                // Non-Tax Sale Invoice prints without WHT by default — so this one starts OFF.
                                <ToggleRow>
                                  <ToggleLabel>Show WHT</ToggleLabel>
                                  <Toggle><input type="checkbox" checked={docSettings.showWHT === true} onChange={(e) => updateDocOverride(dt.key, 'showWHT', e.target.checked)} /><span /></Toggle>
                                </ToggleRow>
                              )}
                              {(dt.key === 'saleInvoice' || dt.key === 'nonTaxSaleInvoice') && (
                                <>
                                  <ToggleRow>
                                    <ToggleLabel>Show Advance Payment</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showAdvancePayment !== false} onChange={(e) => updateDocOverride(dt.key, 'showAdvancePayment', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Transportation / Carriage</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showTransportation !== false} onChange={(e) => updateDocOverride(dt.key, 'showTransportation', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Other Charges</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showOtherCharges !== false} onChange={(e) => updateDocOverride(dt.key, 'showOtherCharges', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                </>
                              )}
                              {(dt.key === 'saleInvoice' || dt.key === 'nonTaxSaleInvoice') && (
                                <>
                                  <FieldGroupTitle>Customer Details</FieldGroupTitle>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer Address</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showCustomerAddress !== false} onChange={(e) => updateDocOverride(dt.key, 'showCustomerAddress', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer Phone</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showCustomerPhone !== false} onChange={(e) => updateDocOverride(dt.key, 'showCustomerPhone', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer Area</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showCustomerArea !== false} onChange={(e) => updateDocOverride(dt.key, 'showCustomerArea', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer Region</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showCustomerRegion !== false} onChange={(e) => updateDocOverride(dt.key, 'showCustomerRegion', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                </>
                              )}
                              {(dt.key === 'purchaseInvoice' || dt.key === 'nonTaxPurchaseInvoice') && (
                                <>
                                  <ToggleRow>
                                    <ToggleLabel>Show Transportation / Carriage</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showTransportation !== false} onChange={(e) => updateDocOverride(dt.key, 'showTransportation', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Other Charges</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showOtherCharges !== false} onChange={(e) => updateDocOverride(dt.key, 'showOtherCharges', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                </>
                              )}

                              {/* Sale Invoice and Non-Tax Sale Invoice — full per-field
                                  show/hide set. Every switch defaults to the current printed output. */}
                              {(dt.key === 'saleInvoice' || dt.key === 'nonTaxSaleInvoice') && (
                                <>
                                  <FieldGroupTitle>Header Fields</FieldGroupTitle>
                                  <ToggleRow>
                                    <ToggleLabel>Show Due Date</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showDueDate !== false} onChange={(e) => updateDocOverride(dt.key, 'showDueDate', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Payment Terms</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showPaymentTerms !== false} onChange={(e) => updateDocOverride(dt.key, 'showPaymentTerms', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Ref Document</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showRefDocument !== false} onChange={(e) => updateDocOverride(dt.key, 'showRefDocument', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer PO #</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showCustomerPO !== false} onChange={(e) => updateDocOverride(dt.key, 'showCustomerPO', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer NTN</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showNTN !== false} onChange={(e) => updateDocOverride(dt.key, 'showNTN', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Customer STRN</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showSTRN !== false} onChange={(e) => updateDocOverride(dt.key, 'showSTRN', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>

                                  <FieldGroupTitle>Item Columns</FieldGroupTitle>
                                  {dt.key === 'saleInvoice' && (
                                    <ToggleRow>
                                      <ToggleLabel>HS Code</ToggleLabel>
                                      <Toggle><input type="checkbox" checked={(docSettings.columns || {}).hsCode !== false} onChange={(e) => updateDocColumn(dt.key, 'hsCode', e.target.checked)} /><span /></Toggle>
                                    </ToggleRow>
                                  )}
                                  <ToggleRow>
                                    <ToggleLabel>Barcode (before Qty)</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={(docSettings.columns || {}).barcode === true} onChange={(e) => updateDocColumn(dt.key, 'barcode', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Unit (UoM)</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={(docSettings.columns || {}).uom !== false} onChange={(e) => updateDocColumn(dt.key, 'uom', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Unit Price</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={(docSettings.columns || {}).rate !== false} onChange={(e) => updateDocColumn(dt.key, 'rate', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>MRP</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={(docSettings.columns || {}).mrp !== false} onChange={(e) => updateDocColumn(dt.key, 'mrp', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Item Image</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={(docSettings.columns || {}).image !== false} onChange={(e) => updateDocColumn(dt.key, 'image', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Source Doc (GRN / DC) columns</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={(docSettings.columns || {}).sourceDoc !== false} onChange={(e) => updateDocColumn(dt.key, 'sourceDoc', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>

                                  <FieldGroupTitle>Totals</FieldGroupTitle>
                                  <ToggleRow>
                                    <ToggleLabel>Show Subtotal</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showSubtotal !== false} onChange={(e) => updateDocOverride(dt.key, 'showSubtotal', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Total Quantity</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showTotalQty === true} onChange={(e) => updateDocOverride(dt.key, 'showTotalQty', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <ToggleRow>
                                    <ToggleLabel>Show Amount in Words</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showAmountInWords === true} onChange={(e) => updateDocOverride(dt.key, 'showAmountInWords', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>

                                  <FieldGroupTitle>Extra Blocks</FieldGroupTitle>
                                  <ToggleRow>
                                    <ToggleLabel>Show Previous / New Balance</ToggleLabel>
                                    <Toggle><input type="checkbox" checked={docSettings.showPreviousBalance === true} onChange={(e) => updateDocOverride(dt.key, 'showPreviousBalance', e.target.checked)} /><span /></Toggle>
                                  </ToggleRow>
                                  <PageDescription>
                                    Prints the customer's balance before this bill, this bill, amount paid,
                                    and the resulting new balance — like the POS receipt. Only meaningful for
                                    named credit customers.
                                  </PageDescription>
                                </>
                              )}
                            </>
                          )}

                          {(dt.key === 'purchaseReturn' || dt.key === 'nonTaxPurchaseReturn') && (
                            <ToggleRow>
                              <ToggleLabel>Show WHT</ToggleLabel>
                              <Toggle><input type="checkbox" checked={docSettings.showWHT !== false} onChange={(e) => updateDocOverride(dt.key, 'showWHT', e.target.checked)} /><span /></Toggle>
                            </ToggleRow>
                          )}

                          {dt.key === 'quotation' && (
                            <>
                              <ToggleRow>
                                <ToggleLabel>Split Stores &amp; Services</ToggleLabel>
                                <Toggle><input type="checkbox" checked={docSettings.splitStoresServices === true} onChange={(e) => updateDocOverride(dt.key, 'splitStoresServices', e.target.checked)} /><span /></Toggle>
                              </ToggleRow>
                              <PageDescription>
                                Prints goods and services as two separate tables — "Stores" for product
                                items and "Services" for service items — each closing with its own Sub
                                Total and its own tax (GST on goods, SST on services), then one Grand
                                Total. Items are sorted by the Product / Service setting on each item in
                                Chart of Inventory, so a service saved as a product will appear under
                                Stores. Off by default, which prints the single table as before.
                              </PageDescription>
                            </>
                          )}

                          {columnDefs.length > 0 && (
                            <>
                              <Label style={{ marginTop: 12, marginBottom: 6 }}>Column Visibility</Label>
                              {columnDefs.map(col => (
                                <ToggleRow key={col.key}>
                                  <ToggleLabel>{col.label}</ToggleLabel>
                                  <Toggle>
                                    <input
                                      type="checkbox"
                                      checked={(docSettings.columns || {})[col.key] !== false}
                                      onChange={(e) => updateDocColumn(dt.key, col.key, e.target.checked)}
                                    />
                                    <span />
                                  </Toggle>
                                </ToggleRow>
                              ))}
                            </>
                          )}
                        </DocOverrideBody>
                      )}
                    </div>
                  );
                })}
              </CardBody>
            </FormCard>

            {/* ─── Letterhead ─── */}
            <FormCard>
              <CardHeader>
                <CardIcon><FiLayers /></CardIcon>
                <CardTitle>Letterhead</CardTitle>
              </CardHeader>
              <CardBody>
                <PageDescription style={{ marginBottom: 16 }}>
                  Upload a Header, Watermark and Footer image to brand your printed documents.
                  When letterhead is on for a document type, the built-in company / branch
                  details are hidden — your uploaded images take their place. Each image is
                  independent: upload only the pieces you need.
                </PageDescription>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
                  {LETTERHEAD_SLOTS.map(slot => {
                    const url = settings.letterhead?.[`${slot.key}Url`];
                    const isUploading = uploadingSlot === slot.key;
                    const inputId = `letterhead-upload-${slot.key}`;
                    return (
                      <div
                        key={slot.key}
                        style={{
                          border: `1px solid ${colors.neutral[200]}`,
                          borderRadius: 8,
                          padding: 14,
                          background: colors.neutral[50],
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: colors.primary.main, fontSize: 16 }}>{slot.icon}</span>
                          <strong style={{ fontSize: 14, color: colors.text.primary }}>{slot.label}</strong>
                        </div>
                        <div style={{ fontSize: 12, color: colors.text.secondary, lineHeight: 1.5 }}>
                          {slot.description}
                        </div>
                        <div style={{ fontSize: 11, color: colors.text.secondary, fontStyle: 'italic' }}>
                          Recommended: {slot.recommended}
                        </div>

                        {url ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{
                              padding: 10,
                              background: 'white',
                              border: `1px solid ${colors.neutral[200]}`,
                              borderRadius: 6,
                              minHeight: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <img
                                src={url}
                                alt={slot.label}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: 100,
                                  objectFit: 'contain',
                                  opacity: slot.key === 'watermark' ? 0.4 : 1,
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <label
                                htmlFor={inputId}
                                style={{
                                  flex: 1,
                                  padding: '6px 10px',
                                  background: 'white',
                                  border: `1px solid ${colors.neutral[300]}`,
                                  borderRadius: 6,
                                  fontSize: 12,
                                  cursor: canEdit && !isUploading ? 'pointer' : 'not-allowed',
                                  textAlign: 'center',
                                  color: colors.text.primary,
                                  opacity: canEdit && !isUploading ? 1 : 0.6,
                                }}
                              >
                                <FiUpload style={{ verticalAlign: -2, marginRight: 4 }} />
                                {isUploading ? 'Uploading…' : 'Replace'}
                              </label>
                              <button
                                type="button"
                                onClick={() => handleLetterheadRemove(slot.key)}
                                disabled={!canEdit || isUploading}
                                style={{
                                  padding: '6px 10px',
                                  background: 'white',
                                  border: `1px solid ${colors.error.main}40`,
                                  borderRadius: 6,
                                  fontSize: 12,
                                  cursor: canEdit && !isUploading ? 'pointer' : 'not-allowed',
                                  color: colors.error.main,
                                  opacity: canEdit && !isUploading ? 1 : 0.5,
                                }}
                              >
                                <FiTrash2 size={12} style={{ verticalAlign: -2 }} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label
                            htmlFor={inputId}
                            style={{
                              padding: '20px 10px',
                              background: 'white',
                              border: `1px dashed ${colors.neutral[300]}`,
                              borderRadius: 6,
                              textAlign: 'center',
                              cursor: canEdit && !isUploading ? 'pointer' : 'not-allowed',
                              color: colors.text.secondary,
                              fontSize: 13,
                              opacity: canEdit && !isUploading ? 1 : 0.6,
                            }}
                          >
                            <FiUpload style={{ display: 'block', margin: '0 auto 6px', fontSize: 18, color: colors.primary.main }} />
                            {isUploading ? 'Uploading…' : 'Click to upload'}
                            <div style={{ fontSize: 10, color: colors.text.disabled, marginTop: 4 }}>
                              PNG · JPG · WebP · max 2 MB
                            </div>
                          </label>
                        )}

                        <input
                          id={inputId}
                          type="file"
                          accept={LETTERHEAD_ACCEPT}
                          disabled={!canEdit || isUploading}
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleLetterheadUpload(slot.key, f);
                            // Reset the input so re-uploading the same filename retriggers onChange
                            e.target.value = '';
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                <SectionDivider />
                <Label style={{ marginBottom: 12 }}>Layout & Watermark Tuning</Label>
                <FormGrid>
                  <FormGroup>
                    <Label>Header Height (mm)</Label>
                    <Input
                      type="number"
                      min={10}
                      max={100}
                      step={1}
                      value={settings.letterhead?.headerHeightMm ?? 45}
                      onChange={(e) => updateLetterhead('headerHeightMm', Number(e.target.value) || 45)}
                    />
                    <div style={{ fontSize: 11, color: colors.text.secondary }}>
                      How much space the header image reserves at the top of every page.
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label>Footer Height (mm)</Label>
                    <Input
                      type="number"
                      min={10}
                      max={80}
                      step={1}
                      value={settings.letterhead?.footerHeightMm ?? 30}
                      onChange={(e) => updateLetterhead('footerHeightMm', Number(e.target.value) || 30)}
                    />
                    <div style={{ fontSize: 11, color: colors.text.secondary }}>
                      Bottom strip height. Content will be padded to clear it.
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label>Watermark Opacity ({((settings.letterhead?.watermarkOpacity ?? 0.08) * 100).toFixed(0)}%)</Label>
                    <Input
                      type="range"
                      min={0.02}
                      max={0.5}
                      step={0.01}
                      value={settings.letterhead?.watermarkOpacity ?? 0.08}
                      onChange={(e) => updateLetterhead('watermarkOpacity', Number(e.target.value) || 0.08)}
                    />
                    <div style={{ fontSize: 11, color: colors.text.secondary }}>
                      Faintness of the centered watermark. 8% is a sensible default.
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label>Watermark Width ({settings.letterhead?.watermarkWidthPct ?? 60}%)</Label>
                    <Input
                      type="range"
                      min={20}
                      max={100}
                      step={5}
                      value={settings.letterhead?.watermarkWidthPct ?? 60}
                      onChange={(e) => updateLetterhead('watermarkWidthPct', Number(e.target.value) || 60)}
                    />
                    <div style={{ fontSize: 11, color: colors.text.secondary }}>
                      Width of the watermark as % of page width.
                    </div>
                  </FormGroup>
                </FormGrid>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Apply Letterhead To</Label>
                <PageDescription style={{ marginBottom: 8, fontSize: 12 }}>
                  Letterhead activates only when both (a) at least one image is uploaded above
                  and (b) the document is checked here. Other document types are unaffected.
                </PageDescription>
                {LETTERHEAD_DOC_TYPES.map(dt => (
                  <ToggleRow key={dt.key}>
                    <ToggleLabel>{dt.name}</ToggleLabel>
                    <Toggle>
                      <input
                        type="checkbox"
                        checked={settings.letterhead?.enabledForDocTypes?.[dt.key] !== false}
                        onChange={() => toggleLetterheadDoc(dt.key)}
                      />
                      <span />
                    </Toggle>
                  </ToggleRow>
                ))}

                <SectionDivider />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {LETTERHEAD_DOC_TYPES.map(dt => (
                    <PreviewButton
                      key={dt.key}
                      type="button"
                      onClick={() => handlePreview(dt.key)}
                    >
                      <FiEye size={12} /> Preview {dt.name}
                    </PreviewButton>
                  ))}
                </div>
              </CardBody>
            </FormCard>

            {/* ─── Invoice Letterheads (advance module) ─── */}
            {invoiceLetterheadsOn && (
              <FormCard>
                <CardHeader>
                  <CardIcon><FiFileText /></CardIcon>
                  <CardTitle>Invoice Letterheads</CardTitle>
                </CardHeader>
                <CardBody>
                  <PageDescription style={{ marginBottom: 16 }}>
                    Extra names to print at the top of a Sale Invoice or Non-Tax Sale Invoice. When you
                    press Print you choose: the company's own details, one of these, or Blank (no company
                    details). Print only — accounts and stock stay under this company.
                  </PageDescription>
                  {invoiceLetterheads.map((l, i) => (
                    <div key={l.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                      <FormGrid>
                        <FormGroup>
                          <Label>Name *</Label>
                          <Input value={l.name || ''} placeholder="e.g. Muhammad Hassan" aria-label={`Letterhead ${i + 1} name`}
                            onChange={(e) => updateInvoiceLetterhead(l.id, 'name', e.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Address</Label>
                          <Input value={l.address || ''} aria-label={`Letterhead ${i + 1} address`}
                            onChange={(e) => updateInvoiceLetterhead(l.id, 'address', e.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Phone / Cell</Label>
                          <Input value={l.phone || ''} aria-label={`Letterhead ${i + 1} phone`}
                            onChange={(e) => updateInvoiceLetterhead(l.id, 'phone', e.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Email</Label>
                          <Input value={l.email || ''} aria-label={`Letterhead ${i + 1} email`}
                            onChange={(e) => updateInvoiceLetterhead(l.id, 'email', e.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>NTN</Label>
                          <Input value={l.ntn || ''} aria-label={`Letterhead ${i + 1} NTN`}
                            onChange={(e) => updateInvoiceLetterhead(l.id, 'ntn', e.target.value)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Sales Tax No.</Label>
                          <Input value={l.salesTaxNo || ''} aria-label={`Letterhead ${i + 1} sales tax`}
                            onChange={(e) => updateInvoiceLetterhead(l.id, 'salesTaxNo', e.target.value)} />
                        </FormGroup>
                      </FormGrid>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <PreviewButton type="button" onClick={() => previewInvoiceLetterhead(l.id)} disabled={!String(l.name || '').trim()}>
                          <FiEye size={12} /> Preview
                        </PreviewButton>
                        {canEdit && (
                          <PreviewButton type="button" onClick={() => removeInvoiceLetterhead(l.id)}>
                            <FiTrash2 size={12} /> Remove
                          </PreviewButton>
                        )}
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {canEdit && invoiceLetterheads.length < MAX_INVOICE_LETTERHEADS && (
                      <PreviewButton type="button" onClick={addInvoiceLetterhead}>
                        <FiPlus size={12} /> Add Letterhead
                      </PreviewButton>
                    )}
                    <PreviewButton type="button" onClick={() => previewInvoiceLetterhead('blank')}>
                      <FiEye size={12} /> Preview Blank
                    </PreviewButton>
                  </div>
                </CardBody>
              </FormCard>
            )}

            {/* ─── POS Receipt ─── */}
            <FormCard>
              <CardHeader>
                <CardIcon><FiPrinter /></CardIcon>
                <CardTitle>POS Receipt</CardTitle>
              </CardHeader>
              <CardBody>
                <PageDescription style={{ marginBottom: 12 }}>
                  Controls the 80mm thermal receipt that prints from the POS screen. Every
                  toggle below maps to a row in the printed slip — leave defaults for the
                  legacy look, or customize for your business.
                </PageDescription>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <PreviewButton type="button" onClick={handlePOSReceiptPreview}>
                    <FiEye size={12} /> Preview POS Receipt
                  </PreviewButton>
                </div>

                <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  <ToggleRow>
                    <ToggleLabel>Bluetooth thermal print (RawBT) — this device only</ToggleLabel>
                    <Toggle><input type="checkbox" checked={rawbtOn} onChange={(e) => handleToggleRawBT(e.target.checked)} /><span /></Toggle>
                  </ToggleRow>
                  <PageDescription style={{ marginTop: 6 }}>
                    Turn ON only on a <strong>mobile device</strong> that prints to a Bluetooth thermal printer through the
                    <strong> RawBT</strong> app. POS receipts on THIS device will then print via RawBT instead of the browser —
                    desktop tills are unaffected. Uses the paper size &amp; logo settings below. <strong>Android only.</strong>
                  </PageDescription>
                  <div style={{ marginTop: 8 }}>
                    <PreviewButton type="button" onClick={handleTestRawBT}>
                      <FiPrinter size={12} /> Test Bluetooth Print
                    </PreviewButton>
                  </div>
                </div>

                <FormGrid>
                  <FormGroup>
                    <Label>Paper Size</Label>
                    <Select
                      value={settings.posReceipt?.paperSize || '80mm'}
                      onChange={(e) => updatePOSReceipt('paperSize', e.target.value)}
                    >
                      <option value="58mm">58 mm (compact thermal)</option>
                      <option value="80mm">80 mm (standard thermal)</option>
                      <option value="A5">A5</option>
                      <option value="A4">A4</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Receipt Title</Label>
                    <Input
                      value={settings.posReceipt?.receiptTitle ?? DEFAULT_POS_RECEIPT.receiptTitle}
                      onChange={(e) => updatePOSReceipt('receiptTitle', e.target.value)}
                      placeholder="Sales Receipt / Tax Invoice / Custom"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Font Size</Label>
                    <Select
                      value={settings.posReceipt?.fontSize || 'medium'}
                      onChange={(e) => updatePOSReceipt('fontSize', e.target.value)}
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Copies</Label>
                    <Select
                      value={settings.posReceipt?.copies || 1}
                      onChange={(e) => updatePOSReceipt('copies', Number(e.target.value))}
                    >
                      <option value={1}>1 (Customer)</option>
                      <option value={2}>2 (Customer + Merchant)</option>
                      <option value={3}>3</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Currency Symbol Position</Label>
                    <Select
                      value={settings.posReceipt?.currencyPosition || 'before'}
                      onChange={(e) => updatePOSReceipt('currencyPosition', e.target.value)}
                    >
                      <option value="before">Before (Rs 100)</option>
                      <option value="after">After (100 Rs)</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Logo Size</Label>
                    <Select
                      value={settings.posReceipt?.logoSize || 'md'}
                      onChange={(e) => updatePOSReceipt('logoSize', e.target.value)}
                      disabled={!settings.posReceipt?.showLogo}
                    >
                      <option value="sm">Small</option>
                      <option value="md">Medium</option>
                      <option value="lg">Large</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Company Header</Label>
                <ToggleRow>
                  <ToggleLabel>Show Logo</ToggleLabel>
                  <Toggle><input type="checkbox" checked={!!settings.posReceipt?.showLogo} onChange={(e) => updatePOSReceipt('showLogo', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Company Name</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showCompanyName !== false} onChange={(e) => updatePOSReceipt('showCompanyName', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Address</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showAddress !== false} onChange={(e) => updatePOSReceipt('showAddress', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Phone</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showPhone !== false} onChange={(e) => updatePOSReceipt('showPhone', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show NTN / CR</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showNTN !== false} onChange={(e) => updatePOSReceipt('showNTN', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show {taxLabel} Number</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showGST !== false} onChange={(e) => updatePOSReceipt('showGST', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Website</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showWebsite !== false} onChange={(e) => updatePOSReceipt('showWebsite', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Document Metadata</Label>
                <ToggleRow>
                  <ToggleLabel>Show Cashier Name</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showCashier !== false} onChange={(e) => updatePOSReceipt('showCashier', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Customer Name (hidden for Walk-in)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showCustomer !== false} onChange={(e) => updatePOSReceipt('showCustomer', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Barcode of Receipt # (for return scanning)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={!!settings.posReceipt?.showBarcode} onChange={(e) => updatePOSReceipt('showBarcode', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Line Item Display</Label>
                <ToggleRow>
                  <ToggleLabel>Show Item Code</ToggleLabel>
                  <Toggle><input type="checkbox" checked={!!settings.posReceipt?.showItemCode} onChange={(e) => updatePOSReceipt('showItemCode', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Unit of Measure</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showUoM !== false} onChange={(e) => updatePOSReceipt('showUoM', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Discount Column</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showDiscountColumn !== false} onChange={(e) => updatePOSReceipt('showDiscountColumn', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Totals & Payment</Label>
                <ToggleRow>
                  <ToggleLabel>Show Subtotal Line</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showSubtotal !== false} onChange={(e) => updatePOSReceipt('showSubtotal', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Tax Breakdown</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showTaxBreakdown !== false} onChange={(e) => updatePOSReceipt('showTaxBreakdown', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Bold Grand Total</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.boldTotal !== false} onChange={(e) => updatePOSReceipt('boldTotal', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Payment Method</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showPaymentMethod !== false} onChange={(e) => updatePOSReceipt('showPaymentMethod', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Tendered & Change (cash sales)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showTenderedChange !== false} onChange={(e) => updatePOSReceipt('showTenderedChange', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Previous Balance (credit customers)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.posReceipt?.showPreviousBalance === true} onChange={(e) => updatePOSReceipt('showPreviousBalance', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Free Text</Label>
                <FormGroup style={{ marginBottom: 12 }}>
                  <Label>Header Text (above receipt body)</Label>
                  <TextArea
                    value={settings.posReceipt?.headerText || ''}
                    onChange={(e) => updatePOSReceipt('headerText', e.target.value)}
                    placeholder='Optional message above the items, e.g. "Welcome to AMAL Mart"'
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: 12 }}>
                  <Label>Footer Message</Label>
                  <TextArea
                    value={settings.posReceipt?.footerText ?? DEFAULT_POS_RECEIPT.footerText}
                    onChange={(e) => updatePOSReceipt('footerText', e.target.value)}
                    placeholder='e.g. "Thank you for shopping with us!"'
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: 12 }}>
                  <Label>Promotional Message</Label>
                  <TextArea
                    value={settings.posReceipt?.promoMessage || ''}
                    onChange={(e) => updatePOSReceipt('promoMessage', e.target.value)}
                    placeholder='e.g. "Visit again on Friday for 20% off"'
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Return Policy</Label>
                  <TextArea
                    value={settings.posReceipt?.returnPolicyText || ''}
                    onChange={(e) => updatePOSReceipt('returnPolicyText', e.target.value)}
                    placeholder='e.g. "Returns accepted within 7 days with this receipt"'
                  />
                </FormGroup>
              </CardBody>
            </FormCard>

            {/* ─── Barcode Labels (Zebra / TSC thermal) ─── */}
            {barcodeLabelsEnabled && (
            <FormCard>
              <CardHeader>
                <CardIcon><FiPrinter /></CardIcon>
                <CardTitle>Barcode Labels (Zebra / TSC)</CardTitle>
              </CardHeader>
              <CardBody>
                <PageDescription style={{ marginBottom: 12 }}>
                  Layout for the bulk barcode-label printer. The print engine forces an
                  exact page size, zero margins, and no trailing pages so Zebra/TSC don't
                  feed blank labels. When printing, always set the browser dialog's
                  <strong> Scale = 100% </strong>and<strong> Margins = None</strong>.
                </PageDescription>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <PreviewButton type="button" onClick={handleBarcodeLabelPreview}>
                    <FiEye size={12} /> Preview 3 Labels
                  </PreviewButton>
                </div>

                <FormGrid>
                  <FormGroup>
                    <Label>Label Size Preset</Label>
                    <Select
                      value={settings.barcodeLabel?.paperPreset || '38x28mm'}
                      onChange={(e) => {
                        const preset = e.target.value;
                        const presetDims = {
                          '38x28mm':         { widthMm: 38, heightMm: 28, columns: 1, columnGapMm: 0 },
                          '2x1in':           { widthMm: 50.8, heightMm: 25.4, columns: 1, columnGapMm: 0 },
                          '2x1in-doubleup':  { widthMm: 50.8, heightMm: 25.4, columns: 2, columnGapMm: 3 },
                        }[preset];
                        setSettings(prev => ({
                          ...prev,
                          barcodeLabel: {
                            ...prev.barcodeLabel,
                            paperPreset: preset,
                            ...(presetDims || {})
                          }
                        }));
                      }}
                    >
                      <option value="38x28mm">38 × 28 mm (typical single)</option>
                      <option value="2x1in">2 × 1 in (50.8 × 25.4 mm)</option>
                      <option value="2x1in-doubleup">2 × 1 in — double-up (2 per row)</option>
                      <option value="custom">Custom</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Barcode Format</Label>
                    <Select
                      value={settings.barcodeLabel?.barcodeFormat || 'CODE128'}
                      onChange={(e) => updateBarcodeLabel('barcodeFormat', e.target.value)}
                    >
                      <option value="CODE128">Code 128 (recommended)</option>
                      <option value="EAN13">EAN-13 (13-digit codes only)</option>
                      <option value="CODE39">Code 39</option>
                      <option value="UPC">UPC-A</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Width (mm)</Label>
                    <Input
                      type="number" min={20} max={150} step={0.1}
                      value={settings.barcodeLabel?.widthMm ?? 38}
                      onChange={(e) => updateBarcodeLabel('widthMm', Number(e.target.value) || 38)}
                      disabled={settings.barcodeLabel?.paperPreset !== 'custom'}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Height (mm)</Label>
                    <Input
                      type="number" min={10} max={150} step={0.1}
                      value={settings.barcodeLabel?.heightMm ?? 28}
                      onChange={(e) => updateBarcodeLabel('heightMm', Number(e.target.value) || 28)}
                      disabled={settings.barcodeLabel?.paperPreset !== 'custom'}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Columns (1 = single, 2 = double-up)</Label>
                    <Input
                      type="number" min={1} max={4} step={1}
                      value={settings.barcodeLabel?.columns ?? 1}
                      onChange={(e) => updateBarcodeLabel('columns', Math.max(1, Math.min(4, Number(e.target.value) || 1)))}
                      disabled={settings.barcodeLabel?.paperPreset !== 'custom'}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Column Gap (mm)</Label>
                    <Input
                      type="number" min={0} max={20} step={0.1}
                      value={settings.barcodeLabel?.columnGapMm ?? 0}
                      onChange={(e) => updateBarcodeLabel('columnGapMm', Number(e.target.value) || 0)}
                      disabled={settings.barcodeLabel?.paperPreset !== 'custom'}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Inner Padding (mm)</Label>
                    <Input
                      type="number" min={0} max={10} step={0.1}
                      value={settings.barcodeLabel?.paddingMm ?? 2}
                      onChange={(e) => updateBarcodeLabel('paddingMm', Number(e.target.value) || 0)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Barcode Height (mm)</Label>
                    <Input
                      type="number" min={5} max={40} step={0.5}
                      value={settings.barcodeLabel?.barcodeHeightMm ?? 10}
                      onChange={(e) => updateBarcodeLabel('barcodeHeightMm', Number(e.target.value) || 10)}
                    />
                  </FormGroup>
                </FormGrid>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Fields on the Label</Label>
                <ToggleRow>
                  <ToggleLabel>Show Company Name</ToggleLabel>
                  <Toggle><input type="checkbox" checked={!!settings.barcodeLabel?.showCompanyName} onChange={(e) => updateBarcodeLabel('showCompanyName', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Item Name</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.barcodeLabel?.showItemName !== false} onChange={(e) => updateBarcodeLabel('showItemName', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Item Code</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.barcodeLabel?.showItemCode !== false} onChange={(e) => updateBarcodeLabel('showItemCode', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Sale Price</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.barcodeLabel?.showSalePrice !== false} onChange={(e) => updateBarcodeLabel('showSalePrice', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show MRP (if item has one)</ToggleLabel>
                  <Toggle><input type="checkbox" checked={!!settings.barcodeLabel?.showMrp} onChange={(e) => updateBarcodeLabel('showMrp', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Barcode Image</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.barcodeLabel?.showBarcode !== false} onChange={(e) => updateBarcodeLabel('showBarcode', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Show Barcode Number Under Image</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.barcodeLabel?.showBarcodeText !== false} onChange={(e) => updateBarcodeLabel('showBarcodeText', e.target.checked)} /><span /></Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLabel>Bold Sale Price</ToggleLabel>
                  <Toggle><input type="checkbox" checked={settings.barcodeLabel?.boldPrice !== false} onChange={(e) => updateBarcodeLabel('boldPrice', e.target.checked)} /><span /></Toggle>
                </ToggleRow>

                <SectionDivider />
                <Label style={{ marginBottom: 8 }}>Font Sizes (pt)</Label>
                <FormGrid>
                  <FormGroup>
                    <Label>Company Name</Label>
                    <Input type="number" min={4} max={20} value={settings.barcodeLabel?.companyFontPt ?? 7}
                      onChange={(e) => updateBarcodeLabel('companyFontPt', Number(e.target.value) || 7)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Item Name</Label>
                    <Input type="number" min={4} max={20} value={settings.barcodeLabel?.itemNameFontPt ?? 9}
                      onChange={(e) => updateBarcodeLabel('itemNameFontPt', Number(e.target.value) || 9)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Item Code</Label>
                    <Input type="number" min={4} max={20} value={settings.barcodeLabel?.itemCodeFontPt ?? 7}
                      onChange={(e) => updateBarcodeLabel('itemCodeFontPt', Number(e.target.value) || 7)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Sale Price</Label>
                    <Input type="number" min={4} max={24} value={settings.barcodeLabel?.priceFontPt ?? 11}
                      onChange={(e) => updateBarcodeLabel('priceFontPt', Number(e.target.value) || 11)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Barcode Number</Label>
                    <Input type="number" min={4} max={14} value={settings.barcodeLabel?.barcodeTextFontPt ?? 7}
                      onChange={(e) => updateBarcodeLabel('barcodeTextFontPt', Number(e.target.value) || 7)} />
                  </FormGroup>
                </FormGrid>

                <SectionDivider />
                <FormGrid>
                  <FormGroup>
                    <Label>Currency Prefix</Label>
                    <Input value={settings.barcodeLabel?.currencyPrefix ?? 'Rs '}
                      onChange={(e) => updateBarcodeLabel('currencyPrefix', e.target.value)}
                      placeholder="e.g. Rs " />
                  </FormGroup>
                  <FormGroup>
                    <Label>Price Label (optional)</Label>
                    <Input value={settings.barcodeLabel?.priceLabel || ''}
                      onChange={(e) => updateBarcodeLabel('priceLabel', e.target.value)}
                      placeholder='e.g. "Price:"' />
                  </FormGroup>
                  <FormGroup>
                    <Label>MRP Label</Label>
                    <Input value={settings.barcodeLabel?.mrpLabel ?? 'MRP'}
                      onChange={(e) => updateBarcodeLabel('mrpLabel', e.target.value)} />
                  </FormGroup>
                </FormGrid>
              </CardBody>
            </FormCard>
            )}

            {/* ─── Item Catalogue ─── */}
            <FormCard>
              <CardHeader>
                <CardIcon><FiPrinter /></CardIcon>
                <CardTitle>Item Catalogue</CardTitle>
              </CardHeader>
              <CardBody>
                <PageDescription style={{ marginBottom: 12 }}>
                  Configure how the product catalogue is printed from the Items page. Pick a design and choose which columns/fields to include.
                </PageDescription>

                <Label style={{ marginBottom: 12 }}>Catalogue Design</Label>
                <TemplateGrid>
                  {CATALOGUE_DESIGNS.map(d => (
                    <TemplateCard
                      key={d.id}
                      $active={(settings.itemCatalogue?.design || 'grid') === d.id}
                      onClick={() => updateDocOverride('itemCatalogue', 'design', d.id)}
                    >
                      <TemplateName>{d.name}</TemplateName>
                      <TemplateDesc>{d.desc}</TemplateDesc>
                    </TemplateCard>
                  ))}
                </TemplateGrid>

                <SectionDivider />
                <ToggleRow>
                  <ToggleLabel>Show Company Header</ToggleLabel>
                  <Toggle>
                    <input
                      type="checkbox"
                      checked={settings.itemCatalogue?.showCompanyHeader !== false}
                      onChange={(e) => updateDocOverride('itemCatalogue', 'showCompanyHeader', e.target.checked)}
                    />
                    <span />
                  </Toggle>
                </ToggleRow>

                <SectionDivider />
                <Label style={{ marginBottom: 6 }}>Columns / Fields to Show</Label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                  {CATALOGUE_COLUMNS.map(col => (
                    <ToggleRow key={col.key}>
                      <ToggleLabel>{col.label}</ToggleLabel>
                      <Toggle>
                        <input
                          type="checkbox"
                          checked={(() => {
                            const saved = settings.itemCatalogue?.columns || {};
                            const defaults = { image: true, itemCode: true, itemName: true, unitOfMeasure: true, salePrice: true };
                            return col.key in saved ? !!saved[col.key] : !!defaults[col.key];
                          })()}
                          onChange={(e) => updateDocColumn('itemCatalogue', col.key, e.target.checked)}
                        />
                        <span />
                      </Toggle>
                    </ToggleRow>
                  ))}
                </div>
              </CardBody>
            </FormCard>

            {canEdit && (
              <FormCard>
                <SaveButtonContainer style={{ border: 'none' }}>
                  <SaveButton type="submit" disabled={saving}>
                    <FiSave />
                    {saving ? 'Saving...' : 'Save Settings'}
                  </SaveButton>
                </SaveButtonContainer>
              </FormCard>
            )}
          </form>
        )}
      </Container>
    </PageLayout>
  );
};

export default PrintSettings;
